import React from "react";
import { ArrowRightIcon, CheckCheckIcon, XIcon } from "lucide-react";
import isNil from "lodash.isnil";

import {
  Button,
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  CarouselApi,
} from "@/components/ui";
import { cn } from "@/helpers/tailwind";
import { OnboardingCarousel, steps } from "./OnboardingCarousel";

interface OnboardingDialogProps {
  className?: string;
  open: boolean;
  step: number;
  onComplete: () => void;
  setOpen: (open: boolean) => void;
  setStep: (step: number) => void;
  onClose?: () => void;
}

export const OnboardingDialog: React.FC<OnboardingDialogProps> = ({
  className,
  open,
  step,
  onComplete,
  setOpen,
  setStep,
  onClose,
}) => {
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();

  React.useEffect(() => {
    if (!carouselApi) {
      return;
    }

    setStep(carouselApi.selectedScrollSnap());
    carouselApi.on("select", () => {
      setStep(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) return;
        setOpen(newOpen);
      }}>
      <DialogOverlay className="opacity-5" />
      <DialogContent
        className={cn(
          "w-[95%] max-w-[1200px] outline-none",
          "overflow-hidden rounded-2xl p-6 pb-8 text-white backdrop-blur-lg",
          className
        )}
        hideCloseButton>
        {!isNil(onClose) && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4"
            onClick={onClose}>
            <XIcon className="size-4" />
          </Button>
        )}
        <DialogHeader
          className={cn(
            "mb-4 mt-6 text-center font-geist",
            "flex flex-col items-center justify-center gap-1"
          )}>
          <DialogTitle className="text-2xl font-bold">
            {steps[step].title}
          </DialogTitle>
          <DialogDescription className="text-white">
            {steps[step].description}
          </DialogDescription>
        </DialogHeader>

        <div className="w-full overflow-hidden">
          <OnboardingCarousel
            useDesktopCarousel
            setCarouselApi={setCarouselApi}
          />
        </div>

        <DialogFooter className="mt-4 w-full !justify-center">
          <Button
            variant="outline"
            disabled={step === 0}
            className="border-slate-200 bg-transparent hover:bg-transparent"
            onClick={() => {
              if (step > 0) {
                carouselApi?.scrollPrev();
              }
            }}>
            Previous
          </Button>
          <Button
            variant="tertiary"
            tabIndex={1}
            onClick={() => {
              if (step === steps.length - 1) {
                onComplete?.();
                setOpen(false);
              } else {
                carouselApi?.scrollNext();
              }
            }}>
            {step === steps.length - 1 ? (
              <>
                <span>Let's go!</span>
                <CheckCheckIcon className="size-4" />
              </>
            ) : (
              <>
                <span>Next</span>
                <ArrowRightIcon className="size-4" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
